import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useSearchParams } from "react-router-dom";

import { showToast } from "@/components/ui";
import {
  clearStoredDrafts,
  readStoredDraftSelections,
  writeStoredDraftSelections,
} from "@/lib/attributeReview/draftStorage";
import { firstFailedMessage } from "@/lib/attributeReview/firstFailedMessage";
import {
  type AttributeReviewApproveSelectedInput,
  type AttributeReviewFieldDecisionInput,
  type AttributeReviewFieldMappingsQuery,
  type AttributeReviewSessionsUnderReviewQuery,
  useAttributeReviewApproveSelectedMutation,
  useAttributeReviewApproveSessionMutation,
  useAttributeReviewFieldMappingsQuery,
  useAttributeReviewSessionsUnderReviewQuery,
  useDeleteMappingSessionMutation,
  useMarketplaceMappingSessionApprovalSyncQuery,
  useRetryTaxonomyApprovalPipelineJobMutation,
  useTaxonomyApprovalPipelineJobsQuery,
} from "@/graphql/generated";
import {
  EMPTY_CONTEXT,
  getInitialFiltersFromUrl,
  resolveAttributeReviewFilterValue,
  toAttributeReviewFilterSlug,
} from "@/lib/attributeReview";
import { deriveMarketplacesFromSessions } from "@/lib/attributeReview/deriveMarketplacesFromSessions";
import { deriveProductTypesFromSessions } from "@/lib/attributeReview/deriveProductTypesFromSessions";
import { fieldDecisionFor } from "@/lib/attributeReview/rowDecision";
import {
  approvalValueResultKey,
  approvalPipelineStateFromMutation,
  approvalPipelineStateFromUpdate,
  getApprovalPipelineFailureMessage,
  getApprovalPipelineStatusLabel,
  isApprovalPipelineActive,
  mergeApprovalPipelineState,
  normalizeApprovalPipelineStatus,
  pipelineUpdateHasTerminalFailure,
  pipelineUpdateHasTerminalSuccess,
  resultMessage,
  type ApprovalPipelineState,
} from "@/lib/attributeReview/approvalPipeline";
import { getMlSuggestedStatusLabel } from "@/lib/attributeReview/getMlSuggestedStatusLabel";
import { mapFieldMappingToRow } from "@/lib/attributeReview/mapFieldMappingToRow";
import { withSameBoxAs } from "@/lib/attributeReview/buildListLine";
import { parseApprovalSyncFieldErrors } from "@/lib/attributeReview/parseApprovalSyncFieldErrors";
import {
  attributeReviewDraftSelectionsEqual,
  draftEqualsSavedAnswer,
} from "@/lib/attributeReview/reviewDraftEquality";
import {
  countSaveAllBlockers,
  sortAttributeReviewMappings,
} from "@/lib/attributeReview/reviewSignals";
import { shouldKeepSessionInReviewFilter } from "@/lib/attributeReview/shouldKeepSessionInReviewFilter";
import {
  isActionableAttributeReviewStatus,
  isApprovedAttributeReviewStatus,
  isRejectedAttributeReviewStatus,
} from "@/lib/attributeReview/reviewQueueStatus";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";

import type {
  AttributeReviewContext,
  AttributeReviewDraftSelection,
  AttributeReviewMappingRow,
  AttributeReviewStagedApproval,
  FilterOption,
} from "./useAttributeReview.types";
import { useApprovalPipelineJobFallbackPoll } from "./useApprovalPipelineJobFallbackPoll";
import {
  useTaxonomyApprovalPipelineSubscription,
  type UseTaxonomyApprovalPipelineSubscriptionOptions,
} from "./useTaxonomyApprovalPipelineSubscription";

export type {
  AttributeReviewContext,
  AttributeReviewDraftSelection,
  AttributeReviewMappingRow,
  AttributeReviewStagedApproval,
  FilterOption,
} from "./useAttributeReview.types";

const normalizeMarketplace = (
  value: string | null | undefined,
): string | null => {
  const normalized = value?.trim();
  return normalized ? normalized.toUpperCase() : null;
};

type ReviewSummaryFilter = "NEEDS_REVIEW" | "LOW_CONFIDENCE";

type LastKnownReviewSession = {
  mappingSessionId: string;
  marketplace: string | null;
  productType: string | null;
  status: string | null;
  needReviewFields: number | null;
};

type ApprovalReviewContext = {
  mappingSessionId?: string | null;
  marketplaceId?: string | null;
  marketplace?: string | null;
  productType?: string | null;
};

function nonBlankString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null;
}

function normalizedContextValue(
  value: string | null | undefined,
): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed.toLowerCase() : null;
}

function approveInputAffectedFieldIds(
  input?: {
    fieldDecisions?: Array<{ sourceFieldId: string }> | null;
  } | null,
): string[] {
  if (!input) return [];
  return uniqueStrings(
    (input.fieldDecisions ?? [])
      .map((decision) => String(decision.sourceFieldId))
      .filter(Boolean),
  );
}

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values.filter((value) => value.length > 0)));
}

function addStringsToSet(current: Set<string>, values: string[]): Set<string> {
  const filteredValues = values.filter((value) => value.length > 0);
  if (filteredValues.length === 0) return current;
  const next = new Set(current);
  filteredValues.forEach((value) => next.add(value));
  return next.size === current.size ? current : next;
}

function removeStringsFromSet(
  current: Set<string>,
  values: string[],
): Set<string> {
  if (values.length === 0 || current.size === 0) return current;
  const next = new Set(current);
  values.forEach((value) => next.delete(value));
  return next.size === current.size ? current : next;
}

function approvalReviewContextFromPipelineState(
  state: ApprovalPipelineState,
  storedContext?: ApprovalReviewContext | null,
): ApprovalReviewContext {
  const payload: Record<string, unknown> =
    state.payload && typeof state.payload === "object" ? state.payload : {};
  return {
    mappingSessionId:
      state.mappingSessionId ??
      storedContext?.mappingSessionId ??
      nonBlankString(payload.mappingSessionId) ??
      nonBlankString(payload.mapping_session_id),
    marketplaceId:
      state.marketplaceId ??
      storedContext?.marketplaceId ??
      nonBlankString(payload.marketplaceId) ??
      nonBlankString(payload.marketplace_id),
    marketplace:
      state.marketplace ??
      storedContext?.marketplace ??
      nonBlankString(payload.marketplace),
    productType:
      state.productType ??
      storedContext?.productType ??
      nonBlankString(payload.productType) ??
      nonBlankString(payload.product_type),
  };
}

function approvalReviewContextsMatch(
  completed: ApprovalReviewContext,
  active: ApprovalReviewContext,
): boolean {
  const completedProductType = normalizedContextValue(completed.productType);
  const activeProductType = normalizedContextValue(active.productType);
  if (!completedProductType || !activeProductType) {
    return Boolean(
      completed.mappingSessionId &&
      active.mappingSessionId &&
      completed.mappingSessionId === active.mappingSessionId,
    );
  }
  if (completedProductType !== activeProductType) return false;

  const completedMarketplaceId = normalizedContextValue(
    completed.marketplaceId,
  );
  const activeMarketplaceId = normalizedContextValue(active.marketplaceId);
  if (
    completedMarketplaceId &&
    activeMarketplaceId &&
    completedMarketplaceId === activeMarketplaceId
  ) {
    return true;
  }

  const completedMarketplace = normalizeMarketplace(completed.marketplace);
  const activeMarketplace = normalizeMarketplace(active.marketplace);
  return Boolean(
    completedMarketplace &&
    activeMarketplace &&
    completedMarketplace === activeMarketplace,
  );
}

type AttributeReviewFieldMappingsPayload = NonNullable<
  AttributeReviewFieldMappingsQuery["attributeReviewFieldMappings"]
>;

function isUsableFieldMappingsPayload(
  payload: AttributeReviewFieldMappingsPayload | null,
): payload is AttributeReviewFieldMappingsPayload {
  return Boolean(
    payload &&
    ((payload.totalFields ?? 0) > 0 || (payload.items?.length ?? 0) > 0),
  );
}
const APPROVE_COPY = {
  missingSessionTitle: "Save failed",
  missingSessionDescription: "No active session to save to",
  notesVerb: "Approved",
  queuedTitle: "Approval queued",
  queuedDescription:
    "Selected mappings were accepted and are now running through the approval pipeline.",
  errorTitle: "Save failed",
  errorFallback: "Failed to approve session",
} as const;

const MAX_TRACKED_APPROVAL_PIPELINES = 12;

function createApprovalIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `approval-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function useAttributeReview() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionIdFromUrl = searchParams.get("sessionId");
  const urlFilters = useMemo(
    () => getInitialFiltersFromUrl(searchParams),
    [searchParams],
  );

  const [selectedMarketplace, setSelectedMarketplace] = useState<string | null>(
    normalizeMarketplace(urlFilters.marketplace),
  );
  const [selectedProductType, setSelectedProductType] = useState<string | null>(
    urlFilters.productType,
  );
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    sessionIdFromUrl ?? null,
  );
  const [lastKnownReviewSession, setLastKnownReviewSession] =
    useState<LastKnownReviewSession | null>(null);
  const [lastUsableFieldMappingsPayload, setLastUsableFieldMappingsPayload] =
    useState<AttributeReviewFieldMappingsPayload | null>(null);
  // Session ids deleted this mount. Gates the field-mappings queries so they
  // can never fetch a session the server has already removed (which 404s as
  // "Mapping session not found"), independent of state-flush timing.
  const [deletedSessionIds, setDeletedSessionIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [draftReviewSelections, setDraftReviewSelections] = useState<
    Record<string, AttributeReviewDraftSelection>
  >({});
  const [draftStorageSessionId, setDraftStorageSessionId] = useState<
    string | null
  >(null);
  const [confidenceSortDirection, setConfidenceSortDirection] = useState<
    "asc" | "desc" | null
  >(null);
  const [mlSuggestedStatusFilter, setMlSuggestedStatusFilter] =
    useState<string>("ALL");
  const [reviewSummaryFilter, setReviewSummaryFilter] =
    useState<ReviewSummaryFilter>("NEEDS_REVIEW");
  // Per-field enum-mapping completeness errors. Populated from a pre-submit
  // local check or from the BE's structured ENUM_MAPPING_INCOMPLETE response.
  const [approvalQueuedFieldIds, setApprovalQueuedFieldIds] = useState<
    Set<string>
  >(new Set());

  // Modal-driven bulk approval: rows the reviewer has approved inside the
  // per-row modal are staged here without hitting the API. Closing the modal
  // triggers a save/discard prompt, or — when every pending row has been
  // approved — auto-commits all staged decisions in a single mutation.
  // In-memory only; reload clears the bucket (draft selections in localStorage
  // survive separately via draftReviewSelections).
  const [approvedBucket, setApprovedBucket] = useState<
    Record<string, AttributeReviewStagedApproval>
  >({});

  useEffect(() => {
    setSelectedSessionId(sessionIdFromUrl ?? null);
  }, [sessionIdFromUrl]);

  const selectedMarketplaceUrlValue = useMemo(
    () =>
      selectedMarketplace
        ? toAttributeReviewFilterSlug(selectedMarketplace)
        : null,
    [selectedMarketplace],
  );
  const selectedProductTypeUrlValue = useMemo(
    () =>
      selectedProductType
        ? toAttributeReviewFilterSlug(selectedProductType)
        : null,
    [selectedProductType],
  );

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (selectedMarketplaceUrlValue) {
      next.set("marketplace", selectedMarketplaceUrlValue);
    } else {
      next.delete("marketplace");
    }
    if (selectedProductTypeUrlValue) {
      next.set("productType", selectedProductTypeUrlValue);
    } else {
      next.delete("productType");
    }

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [
    searchParams,
    selectedMarketplaceUrlValue,
    selectedProductTypeUrlValue,
    setSearchParams,
  ]);

  const removeSessionIdFromUrl = useCallback(() => {
    if (!searchParams.has("sessionId")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("sessionId");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const updateFilterParams = useCallback(
    (filters: {
      marketplace?: string | null;
      productType?: string | null;
      clearSessionId?: boolean;
    }) => {
      const next = new URLSearchParams(searchParams);
      if (filters.clearSessionId) {
        next.delete("sessionId");
      }
      if ("marketplace" in filters) {
        if (filters.marketplace) {
          next.set(
            "marketplace",
            toAttributeReviewFilterSlug(filters.marketplace),
          );
        } else {
          next.delete("marketplace");
        }
      }
      if ("productType" in filters) {
        if (filters.productType) {
          next.set(
            "productType",
            toAttributeReviewFilterSlug(filters.productType),
          );
        } else {
          next.delete("productType");
        }
      }
      if (next.toString() !== searchParams.toString()) {
        setSearchParams(next, { replace: true });
      }
    },
    [searchParams, setSearchParams],
  );

  const toggleMarketplace = useCallback(
    (val: string | null) => {
      const next = normalizeMarketplace(val);
      const nextMarketplace = selectedMarketplace === next ? null : next;
      setSelectedSessionId(null);
      updateFilterParams({
        marketplace: nextMarketplace,
        productType: null,
        clearSessionId: true,
      });
      setSelectedMarketplace(nextMarketplace);
      setSelectedProductType(null);
      setSelectedIds(new Set());
      setExpandedId(null);
      setApprovedBucket({});
    },
    [selectedMarketplace, updateFilterParams],
  );

  const toggleProductType = useCallback(
    (val: string | null) => {
      const nextProductType = selectedProductType === val ? null : val;
      setSelectedSessionId(null);
      updateFilterParams({
        productType: nextProductType,
        clearSessionId: true,
      });
      setSelectedProductType(nextProductType);
      setSelectedIds(new Set());
      setExpandedId(null);
      setApprovedBucket({});
    },
    [selectedProductType, updateFilterParams],
  );

  const { data: sessionsData, isLoading: sessionsLoading } =
    useAttributeReviewSessionsUnderReviewQuery(
      {},
      {
        // No background polling. Session-list updates are pushed via:
        //   - taxonomyMappingSession subscription (REVIEW_READY → invalidates this query)
        //   - the approveSession mutation
        // Polling here was firing every 3s while the list was empty, generating
        // hundreds of redundant requests with no UX benefit.
        refetchOnWindowFocus: false,
        staleTime: 60_000,
      },
    );

  const allSessionsUnderReview = useMemo(
    () => sessionsData?.attributeReviewSessionsUnderReview ?? [],
    [sessionsData?.attributeReviewSessionsUnderReview],
  );

  // Include sessions that either have fields needing review OR are awaiting
  // final approval (e.g. all fields auto-matched at 100% confidence but still
  // require a human sign-off to be finalized).
  const sessionsNeedingReview = useMemo(
    () =>
      allSessionsUnderReview.filter((s) =>
        shouldKeepSessionInReviewFilter(s, {
          marketplace: selectedMarketplace,
          productType: selectedProductType,
        }),
      ),
    [allSessionsUnderReview, selectedMarketplace, selectedProductType],
  );

  const marketplaces = useMemo(
    () => deriveMarketplacesFromSessions(sessionsNeedingReview),
    [sessionsNeedingReview],
  ) as FilterOption[];
  const productTypes = useMemo(() => {
    const relevantSessions = selectedMarketplace
      ? sessionsNeedingReview.filter(
          (s) =>
            s.marketplace?.toUpperCase() === selectedMarketplace.toUpperCase(),
        )
      : sessionsNeedingReview;
    return deriveProductTypesFromSessions(relevantSessions);
  }, [sessionsNeedingReview, selectedMarketplace]) as FilterOption[];

  // Resolve URL slugs against loaded API options, then fall back to the first
  // available marketplace so the review page always has a concrete context.
  useEffect(() => {
    setSelectedMarketplace((prev) => {
      if (marketplaces.length === 0) return prev;
      const fromUrl = resolveAttributeReviewFilterValue(
        marketplaces,
        urlFilters.marketplace,
      );
      if (fromUrl) return fromUrl;
      const current = resolveAttributeReviewFilterValue(marketplaces, prev);
      return current ?? marketplaces[0].name;
    });
  }, [marketplaces, urlFilters.marketplace]);

  // Keep selectedProductType valid, resolving URL slugs such as
  // "bed-linen-set" back to the exact product type returned by the API.
  useEffect(() => {
    setSelectedProductType((prev) => {
      if (productTypes.length === 0) return prev;
      const fromUrl = resolveAttributeReviewFilterValue(
        productTypes,
        urlFilters.productType,
      );
      if (fromUrl) return fromUrl;
      const current = resolveAttributeReviewFilterValue(productTypes, prev);
      return current ?? productTypes[0].name;
    });
  }, [productTypes, urlFilters.productType]);

  const sessionsUnderReview = useMemo(() => {
    return allSessionsUnderReview.filter((session) => {
      let matches = true;
      if (
        selectedMarketplace &&
        session.marketplace?.toUpperCase() !== selectedMarketplace.toUpperCase()
      ) {
        matches = false;
      }
      if (selectedProductType && session.productType !== selectedProductType) {
        matches = false;
      }
      return matches;
    });
  }, [allSessionsUnderReview, selectedMarketplace, selectedProductType]);

  const sessionFromList = useMemo(() => {
    if (selectedSessionId) {
      return (
        sessionsUnderReview.find(
          (session) => session.mappingSessionId === selectedSessionId,
        ) ?? null
      );
    }
    return sessionsUnderReview[0] ?? null;
  }, [selectedSessionId, sessionsUnderReview]);

  const lastKnownSessionMatchesFilters = useMemo(() => {
    if (!lastKnownReviewSession) return false;
    if (
      selectedMarketplace &&
      normalizeMarketplace(lastKnownReviewSession.marketplace) !==
        selectedMarketplace
    ) {
      return false;
    }
    if (
      selectedProductType &&
      lastKnownReviewSession.productType !== selectedProductType
    ) {
      return false;
    }
    return true;
  }, [lastKnownReviewSession, selectedMarketplace, selectedProductType]);

  const currentSessionId =
    selectedSessionId ??
    sessionFromList?.mappingSessionId ??
    (lastKnownSessionMatchesFilters
      ? (lastKnownReviewSession?.mappingSessionId ?? null)
      : null);
  const currentSession = useMemo(
    () =>
      sessionsUnderReview.find(
        (session) => session.mappingSessionId === currentSessionId,
      ) ?? null,
    [currentSessionId, sessionsUnderReview],
  );
  useEffect(() => {
    // Drop the previous session's pins and restore this one's unconfirmed
    // parks: a terminal job whose refetch failed keeps its rows locked until
    // fresh data lands, even across a session switch.
    setApprovalQueuedFieldIds((prev) => {
      const parked = currentSessionId
        ? pendingClearQueuedFieldIdsRef.current.get(currentSessionId)
        : null;
      if (!parked || parked.size === 0) {
        return prev.size > 0 ? new Set<string>() : prev;
      }
      return new Set(parked.keys());
    });
  }, [currentSessionId]);
  const currentSessionStatus =
    currentSession?.status ??
    (lastKnownSessionMatchesFilters &&
    lastKnownReviewSession?.mappingSessionId === currentSessionId
      ? lastKnownReviewSession.status
      : null);
  const isCurrentSessionApprovalSyncFailed =
    currentSessionStatus?.toUpperCase() === "APPROVAL_SYNC_FAILED";
  const selectedSummarySession =
    currentSession ??
    sessionFromList ??
    (lastKnownSessionMatchesFilters ? lastKnownReviewSession : null);

  // In-session retryApprovalPipeline needs the original request payload (lost
  // on reload); resume from the server-persisted retryable job instead.
  const { data: approvalPipelineJobsData } =
    useTaxonomyApprovalPipelineJobsQuery(
      { input: { mappingSessionId: currentSessionId ?? undefined } },
      {
        enabled:
          Boolean(currentSessionId) && isCurrentSessionApprovalSyncFailed,
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
    );
  const resumableFailedApprovalJob = useMemo(() => {
    const jobs =
      approvalPipelineJobsData?.taxonomyApprovalPipelineJobs.items ?? [];
    return (
      [...jobs]
        .filter((job) => job.retryable)
        .sort((left, right) =>
          String(right.updatedAt ?? "").localeCompare(
            String(left.updatedAt ?? ""),
          ),
        )[0] ?? null
    );
  }, [approvalPipelineJobsData]);
  const retryApprovalJobMutation =
    useRetryTaxonomyApprovalPipelineJobMutation();

  // The failed session's real error lives on approvalSync (engine validation
  // message + per-field details) — fetch it only while the banner is shown.
  const { data: failedApprovalSyncData } =
    useMarketplaceMappingSessionApprovalSyncQuery(
      { mappingSessionId: currentSessionId ?? "" },
      {
        enabled:
          Boolean(currentSessionId) && isCurrentSessionApprovalSyncFailed,
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
    );
  const failedApprovalSync = isCurrentSessionApprovalSyncFailed
    ? (failedApprovalSyncData?.marketplaceMappingSession?.approvalSync ?? null)
    : null;
  const approvalSyncError = failedApprovalSync?.error ?? null;
  const approvalSyncFieldErrors = useMemo(
    () => parseApprovalSyncFieldErrors(failedApprovalSync?.errorDetails),
    [failedApprovalSync?.errorDetails],
  );

  useEffect(() => {
    const session = currentSession ?? sessionFromList;
    if (!session?.mappingSessionId) return;
    setLastKnownReviewSession((prev) => {
      const next = {
        mappingSessionId: session.mappingSessionId,
        marketplace: session.marketplace ?? null,
        productType: session.productType ?? null,
        status: session.status ?? null,
        needReviewFields: session.needReviewFields ?? null,
      };
      if (
        prev?.mappingSessionId === next.mappingSessionId &&
        prev.marketplace === next.marketplace &&
        prev.productType === next.productType &&
        prev.status === next.status &&
        prev.needReviewFields === next.needReviewFields
      ) {
        return prev;
      }
      return next;
    });
  }, [currentSession, sessionFromList]);

  useEffect(() => {
    setDraftReviewSelections(readStoredDraftSelections(currentSessionId));
    setDraftStorageSessionId(currentSessionId);
    setApprovedBucket({});
    // approvalQueuedFieldIds is reset (and parked pins restored) by the
    // session-switch effect above — resetting it again here would wipe that
    // restoration.
  }, [currentSessionId]);

  useEffect(() => {
    if (draftStorageSessionId !== currentSessionId) return;
    writeStoredDraftSelections(currentSessionId, draftReviewSelections);
  }, [currentSessionId, draftReviewSelections, draftStorageSessionId]);

  const {
    data: fieldMappingsData,
    dataUpdatedAt: fieldMappingsDataUpdatedAt,
    isLoading: rawFieldMappingsLoading,
    isFetching: fieldMappingsFetching,
    isError: fieldMappingsError,
  } = useAttributeReviewFieldMappingsQuery(
    { sessionId: currentSessionId ?? "" },
    {
      enabled: !!currentSessionId && !deletedSessionIds.has(currentSessionId),
      // The upstream Python call is expensive (multi-second). Don't refetch
      // on every tab focus, and let mutation onSuccess handlers be the
      // source of truth for invalidation.
      refetchOnWindowFocus: false,
      // Socket drops are the #1 cause of a missed REVIEW_READY rerun-terminal
      // event (the only signal that pulls fresh mappings into the cache). When
      // the connection comes back, refetch so the UI self-heals instead of
      // showing a stale "No match found" row until staleTime expires.
      refetchOnReconnect: true,
      staleTime: 5 * 60 * 1000,
      // Keep the previous payload mounted while a refetch (e.g. the rerun
      // terminal invalidation) is in flight. Without this, `data` drops to
      // undefined mid-refetch, the mapping rows remount, and the ML Suggested
      // column flickers old→new→old. keepPreviousData holds the last good
      // data so rows stay stable until the fresh payload lands.
      placeholderData: keepPreviousData,
    },
  );

  const queryClient = useQueryClient();
  const [approvalPipelinesByKey, setApprovalPipelinesByKey] = useState<
    Record<string, ApprovalPipelineState>
  >({});
  const [approvalPipelineOrder, setApprovalPipelineOrder] = useState<string[]>(
    [],
  );
  // Mirror read by the order updater to tell live jobs from settled ones.
  // Staleness only over-retains history, never evicts a live job.
  const approvalPipelinesByKeyRef = useRef(approvalPipelinesByKey);
  useEffect(() => {
    approvalPipelinesByKeyRef.current = approvalPipelinesByKey;
  }, [approvalPipelinesByKey]);
  const approvalInputsByJobIdRef = useRef(
    new Map<string, AttributeReviewApproveSelectedInput>(),
  );
  const approvalContextsByJobIdRef = useRef(
    new Map<string, ApprovalReviewContext>(),
  );
  const activeReviewContextRef = useRef<ApprovalReviewContext>({
    mappingSessionId: null,
    marketplaceId: null,
    marketplace: null,
    productType: null,
  });
  const saveAllJobIdsRef = useRef(new Set<string>());
  const handledTerminalApprovalJobIdsRef = useRef(new Set<string>());
  // Rows whose terminal-event refetch came back stale: they stay pinned until
  // that session's field-mappings QUERY reports data newer than the park time
  // (a stale clear would flash the row into Approved). Keyed by SESSION so a
  // background job's parks survive a session switch and re-pin on revisit:
  // sessionId -> (fieldId -> parkedAt).
  const pendingClearQueuedFieldIdsRef = useRef(
    new Map<string, Map<string, number>>(),
  );
  const parkPendingClears = useCallback(
    (sessionId: string, fieldIds: string[], parkedAt: number) => {
      if (!sessionId || fieldIds.length === 0) return;
      const bySession =
        pendingClearQueuedFieldIdsRef.current.get(sessionId) ??
        new Map<string, number>();
      fieldIds.forEach((id) => bySession.set(id, parkedAt));
      pendingClearQueuedFieldIdsRef.current.set(sessionId, bySession);
      // Parking lands after the refetch resolves; a session switch in that
      // window already cleared the pins, so re-apply them when this park is
      // for the session on screen.
      if (sessionId === activeReviewContextRef.current.mappingSessionId) {
        setApprovalQueuedFieldIds((prev) => addStringsToSet(prev, fieldIds));
      }
    },
    [],
  );
  const forgetPendingClears = useCallback(
    (sessionId: string, fieldIds: string[]) => {
      const bySession = pendingClearQueuedFieldIdsRef.current.get(sessionId);
      if (!bySession) return;
      fieldIds.forEach((id) => bySession.delete(id));
      if (bySession.size === 0) {
        pendingClearQueuedFieldIdsRef.current.delete(sessionId);
      }
    },
    [],
  );

  const upsertApprovalPipeline = useCallback(
    (nextState: ApprovalPipelineState) => {
      const stateKey =
        nextState.syncJobId ??
        `inline:${nextState.mappingSessionId}:${nextState.occurredAt ?? Date.now()}:${nextState.status}`;
      setApprovalPipelinesByKey((prev) => ({
        ...prev,
        [stateKey]: mergeApprovalPipelineState(
          prev[stateKey] ?? null,
          nextState,
        ),
      }));
      setApprovalPipelineOrder((prev) => {
        const withoutCurrent = prev.filter((key) => key !== stateKey);
        const next = [...withoutCurrent, stateKey];
        const overflow = next.length - MAX_TRACKED_APPROVAL_PIPELINES;
        if (overflow <= 0) return next;
        // The cap trims display history only — evicting a LIVE job would drop
        // its subscription, so its terminal event never arrives and its rows
        // stay pinned forever. Settled jobs are the only eviction candidates.
        const byKey = approvalPipelinesByKeyRef.current;
        const dropped = new Set(
          next
            .filter(
              (key) =>
                key !== stateKey && !isApprovalPipelineActive(byKey[key]),
            )
            .slice(0, overflow),
        );
        return dropped.size > 0
          ? next.filter((key) => !dropped.has(key))
          : next;
      });
      return stateKey;
    },
    [],
  );

  const approvalPipelines = useMemo(
    () =>
      approvalPipelineOrder
        .map((key) => approvalPipelinesByKey[key])
        .filter((value): value is ApprovalPipelineState => Boolean(value)),
    [approvalPipelineOrder, approvalPipelinesByKey],
  );
  const activeApprovalPipelines = useMemo(
    () =>
      approvalPipelines.filter((pipeline) =>
        isApprovalPipelineActive(pipeline),
      ),
    [approvalPipelines],
  );
  const currentSessionApprovalPipelines = useMemo(
    () =>
      currentSessionId
        ? approvalPipelines.filter(
            (pipeline) => pipeline.mappingSessionId === currentSessionId,
          )
        : [],
    [approvalPipelines, currentSessionId],
  );
  // A session switch clears the queued set (field ids repeat across sessions),
  // but an approval job can still be running for the session we return to —
  // re-pin its rows so a queue-time-approved row can't be resubmitted.
  useEffect(() => {
    if (!currentSessionId) return;
    const ids: string[] = [];
    for (const pipeline of activeApprovalPipelines) {
      if (pipeline.mappingSessionId !== currentSessionId || !pipeline.syncJobId)
        continue;
      const input = approvalInputsByJobIdRef.current.get(pipeline.syncJobId);
      ids.push(...approveInputAffectedFieldIds(input));
    }
    if (ids.length > 0) {
      setApprovalQueuedFieldIds((prev) => addStringsToSet(prev, ids));
    }
  }, [activeApprovalPipelines, currentSessionId]);
  const currentSessionActiveApprovalPipelines = useMemo(
    () =>
      currentSessionApprovalPipelines.filter((pipeline) =>
        isApprovalPipelineActive(pipeline),
      ),
    [currentSessionApprovalPipelines],
  );
  const activeApprovalPipelineSyncJobIds = useMemo(
    () =>
      activeApprovalPipelines.flatMap((pipeline) =>
        pipeline.syncJobId ? [pipeline.syncJobId] : [],
      ),
    [activeApprovalPipelines],
  );
  const approvalPipelineActiveCount = activeApprovalPipelines.length;
  const latestCurrentSessionApprovalPipeline = useMemo(
    () =>
      currentSessionApprovalPipelines[
        currentSessionApprovalPipelines.length - 1
      ] ?? null,
    [currentSessionApprovalPipelines],
  );
  const retryableApprovalPipeline = useMemo(
    () =>
      [...currentSessionApprovalPipelines]
        .reverse()
        .find(
          (pipeline) =>
            pipelineUpdateHasTerminalFailure(pipeline) && pipeline.retryable,
        ) ?? null,
    [currentSessionApprovalPipelines],
  );
  const approvalPipeline = useMemo<ApprovalPipelineState | null>(() => {
    if (currentSessionApprovalPipelines.length === 0) return null;
    const basePipeline =
      currentSessionActiveApprovalPipelines[
        currentSessionActiveApprovalPipelines.length - 1
      ] ?? latestCurrentSessionApprovalPipeline;
    if (!basePipeline) return null;

    const fieldResultsByFieldId = new Map<
      string,
      ApprovalPipelineState["fieldResults"][number]
    >();
    currentSessionApprovalPipelines.forEach((pipeline) => {
      pipeline.fieldResults.forEach((result) => {
        fieldResultsByFieldId.set(result.sourceFieldId, result);
      });
    });

    const valueResultsByKey = new Map<
      string,
      ApprovalPipelineState["valueResults"][number]
    >();
    currentSessionApprovalPipelines.forEach((pipeline) => {
      pipeline.valueResults.forEach((result) => {
        const key =
          approvalValueResultKey(result) ??
          `${result.sourceFieldId ?? "unknown"}:${result.marketplaceValue ?? result.valueApprovalId ?? "value"}`;
        valueResultsByKey.set(key, result);
      });
    });

    return {
      ...basePipeline,
      fieldResults: Array.from(fieldResultsByFieldId.values()),
      valueResults: Array.from(valueResultsByKey.values()),
    };
  }, [
    currentSessionActiveApprovalPipelines,
    currentSessionApprovalPipelines,
    latestCurrentSessionApprovalPipeline,
  ]);

  const approveSelectedMutation = useAttributeReviewApproveSelectedMutation();
  const approveSessionMutation = useAttributeReviewApproveSessionMutation();
  const deleteMappingSessionMutation = useDeleteMappingSessionMutation({
    onSuccess: (_data, variables) => {
      const sessionId = variables.input.sessionId;
      // Mark deleted first so the field-mappings queries are gated off this id
      // on the very next render — no fetch can slip through to a session the
      // server has already removed.
      setDeletedSessionIds((prev) => {
        if (prev.has(sessionId)) return prev;
        const next = new Set(prev);
        next.add(sessionId);
        return next;
      });
      // Drop the deleted session from the under-review list, drop its cached
      // field mappings, and clear the selection so the UI moves off it.
      queryClient.setQueriesData<AttributeReviewSessionsUnderReviewQuery>(
        { queryKey: ["AttributeReviewSessionsUnderReview"] },
        (current) => {
          if (!current?.attributeReviewSessionsUnderReview) return current;
          return {
            ...current,
            attributeReviewSessionsUnderReview:
              current.attributeReviewSessionsUnderReview.filter(
                (session) => session.mappingSessionId !== sessionId,
              ),
          };
        },
      );
      queryClient.invalidateQueries({
        queryKey: ["AttributeReviewSessionsUnderReview"],
      });
      // Abort any in-flight field-mappings fetch for the deleted session, but
      // do NOT removeQueries here: the observer is still mounted+enabled (its
      // currentSessionId hasn't re-rendered to null yet), and removeQueries on
      // an active key makes React Query immediately refetch the deleted id
      // ("Mapping session not found"). cancelQueries just aborts; the query
      // goes disabled on the next render and its stale cache is GC'd.
      void queryClient.cancelQueries({
        queryKey: ["AttributeReviewFieldMappings", { sessionId }],
      });
      setSelectedSessionId(null);
      // Clear the remembered session/payload if they point at the deleted
      // session; otherwise currentSessionId falls back to the stale
      // lastKnownReviewSession and re-fires the field-mappings query against a
      // now-deleted id ("Mapping session not found").
      setLastKnownReviewSession((prev) =>
        prev?.mappingSessionId === sessionId ? null : prev,
      );
      setLastUsableFieldMappingsPayload((prev) =>
        prev?.mappingSessionId === sessionId ? null : prev,
      );
      // Drop stale row selection/expansion so they don't carry into the next
      // session (currentSessionId falls back to the next under-review session).
      setSelectedIds(new Set());
      setExpandedId(null);
      showToast.success(
        "Mapping deleted",
        "The under-review mapping and its data were removed.",
      );
    },
    onError: (err) => {
      showToast.error(
        "Delete failed",
        err instanceof Error ? err.message : "Failed to delete the mapping.",
      );
    },
  });

  const rawApiPayload = fieldMappingsData?.attributeReviewFieldMappings ?? null;
  useEffect(() => {
    if (
      rawApiPayload?.mappingSessionId === currentSessionId &&
      isUsableFieldMappingsPayload(rawApiPayload)
    ) {
      setLastUsableFieldMappingsPayload(rawApiPayload);
    }
  }, [currentSessionId, rawApiPayload]);

  // With keepPreviousData on the query, `rawApiPayload` can momentarily hold
  // the PREVIOUS session's payload right after currentSessionId changes (until
  // the new fetch resolves). Require the session id to match before trusting it
  // as the live payload; otherwise fall back to the last-usable payload for the
  // current session (or null) so we never render a stale session's mappings.
  const apiPayload =
    rawApiPayload?.mappingSessionId === currentSessionId &&
    isUsableFieldMappingsPayload(rawApiPayload)
      ? rawApiPayload
      : lastUsableFieldMappingsPayload?.mappingSessionId === currentSessionId
        ? lastUsableFieldMappingsPayload
        : null;
  const hasFieldMappingsPayload = Boolean(apiPayload);
  // Zero field mappings (e.g. a reopened session whose finalize consumed the
  // review data) is a terminal answer, not a pending load.
  const fieldMappingsEmpty = Boolean(
    currentSessionId &&
    rawApiPayload?.mappingSessionId === currentSessionId &&
    !isUsableFieldMappingsPayload(rawApiPayload),
  );
  const fieldMappingsLoading =
    rawFieldMappingsLoading ||
    (fieldMappingsFetching && !apiPayload && !fieldMappingsEmpty) ||
    (Boolean(currentSessionId) &&
      !apiPayload &&
      !fieldMappingsError &&
      !fieldMappingsEmpty);
  const apiPayloadMarketplace = apiPayload?.marketplace ?? null;
  const apiPayloadProductType = apiPayload?.productType ?? null;
  useEffect(() => {
    if (
      !currentSessionId ||
      rawFieldMappingsLoading ||
      fieldMappingsError ||
      fieldMappingsData === undefined ||
      rawApiPayload ||
      lastUsableFieldMappingsPayload?.mappingSessionId === currentSessionId
    ) {
      return;
    }

    setSelectedSessionId(null);
    removeSessionIdFromUrl();
    void queryClient.invalidateQueries({
      queryKey: useAttributeReviewSessionsUnderReviewQuery.getKey({}),
    });
  }, [
    currentSessionId,
    fieldMappingsData,
    fieldMappingsError,
    lastUsableFieldMappingsPayload?.mappingSessionId,
    queryClient,
    rawApiPayload,
    rawFieldMappingsLoading,
    removeSessionIdFromUrl,
  ]);
  useEffect(() => {
    if (!selectedSessionId) return;
    if (apiPayloadMarketplace) {
      setSelectedMarketplace(normalizeMarketplace(apiPayloadMarketplace));
    }
    if (apiPayloadProductType) {
      setSelectedProductType(apiPayloadProductType);
    }
  }, [apiPayloadMarketplace, apiPayloadProductType, selectedSessionId]);

  const marketplace = apiPayload?.marketplace ?? selectedMarketplace ?? null;
  const marketplaceId = apiPayload?.marketplaceId ?? marketplace;
  activeReviewContextRef.current = {
    mappingSessionId: currentSessionId,
    marketplaceId: marketplaceId ?? null,
    marketplace,
    productType:
      apiPayloadProductType ??
      selectedProductType ??
      currentSession?.productType,
  };

  const refreshAttributeReviewData = useCallback(
    (sessionId: string) => {
      const fieldMappingsKey = useAttributeReviewFieldMappingsQuery.getKey({
        sessionId,
      });
      const sessionsKey = useAttributeReviewSessionsUnderReviewQuery.getKey({});

      // `invalidateQueries({ refetchType: "all" })` already refetches every
      // matching observer. The previous code ALSO called refetchQueries on the
      // same keys, firing two network round-trips and two cache writes per
      // refresh — each write churns object refs and re-triggers the row reset
      // effect, which is a flicker source. One invalidation per key is enough.
      // Returned so callers can order work after the refetch settles (the
      // failed-approval handler keeps rows pinned until then).
      const fieldMappingsRefresh = Promise.allSettled([
        queryClient.invalidateQueries({
          queryKey: fieldMappingsKey,
          exact: true,
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: sessionsKey,
          exact: true,
          refetchType: "all",
        }),
      ]);

      return fieldMappingsRefresh;
    },
    [queryClient],
  );

  const refreshAttributeReviewFilters = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: useAttributeReviewSessionsUnderReviewQuery.getKey(),
      refetchType: "all",
    });
  }, [queryClient]);

  // "Freshly fetched" = a real network refetch SUCCEEDED after `since`.
  // dataUpdatedAt alone is spoofable — local setQueriesData writes advance it
  // too — but a failed refetch leaves fetchFailureCount > 0 until a genuine
  // fetch succeeds, so the pair is a reliable success signal.
  const fieldMappingsFreshlyFetched = useCallback(
    (sessionId: string, since: number) => {
      const state = queryClient.getQueryState(
        useAttributeReviewFieldMappingsQuery.getKey({ sessionId }),
      );
      return (
        !!state &&
        state.status === "success" &&
        // Not still in flight: a concurrent local write can advance
        // dataUpdatedAt while the network request is pending, and
        // fetchFailureCount stays 0 until a request actually fails.
        state.fetchStatus === "idle" &&
        (state.fetchFailureCount ?? 0) === 0 &&
        state.dataUpdatedAt >= since
      );
    },
    [queryClient],
  );

  const apiMappings = useMemo(() => {
    if (!apiPayload?.items) return [];
    return withSameBoxAs(apiPayload.items.map(mapFieldMappingToRow));
  }, [apiPayload?.items]);

  const mlSuggestedStatusFilterOptions = useMemo(() => {
    const counts = new Map<string, number>();
    apiMappings.forEach((mapping) => {
      const label = getMlSuggestedStatusLabel(mapping);
      counts.set(label, (counts.get(label) ?? 0) + 1);
    });
    return Array.from(counts.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([name, count]) => ({ id: name, name, count }));
  }, [apiMappings]);
  const statusFilteredMappings = useMemo(() => {
    if (mlSuggestedStatusFilter === "ALL") return apiMappings;
    return apiMappings.filter(
      (mapping) =>
        getMlSuggestedStatusLabel(mapping) === mlSuggestedStatusFilter,
    );
  }, [apiMappings, mlSuggestedStatusFilter]);
  // Queued rows stay pinned in Needs Review (rendered as Approving…) until the
  // pipeline job's terminal event — the server flips decisionStatus at queue
  // time, so without the pin a mid-run refetch would move the row to Approved
  // while the job can still fail.
  const baseUnmatchedMappings = useMemo(
    () =>
      statusFilteredMappings.filter((mapping) => {
        const mappingId = String(mapping.id ?? "");
        return (
          approvalQueuedFieldIds.has(mappingId) ||
          isActionableAttributeReviewStatus(mapping.decisionStatus)
        );
      }),
    [approvalQueuedFieldIds, statusFilteredMappings],
  );
  const baseMatchedMappings = useMemo(
    () =>
      statusFilteredMappings.filter(
        (mapping) =>
          !approvalQueuedFieldIds.has(String(mapping.id)) &&
          (isApprovedAttributeReviewStatus(mapping.decisionStatus) ||
            isRejectedAttributeReviewStatus(mapping.decisionStatus)),
      ),
    [approvalQueuedFieldIds, statusFilteredMappings],
  );
  // Unfiltered by the ML-status / low-confidence view: these drive the summary
  // badge and the Complete-Review gate, so a display filter can't hide actionable rows.
  // A queued row is still settling: the summary must agree with the table,
  // which keeps it under Needs Review (Approving…) until the job terminates.
  // Counting its queue-time APPROVED status instead would report 0 needs
  // review and a premature Approved bump mid-job.
  const sessionActionableMappings = useMemo(
    () =>
      apiMappings.filter(
        (mapping) =>
          approvalQueuedFieldIds.has(String(mapping.id)) ||
          isActionableAttributeReviewStatus(mapping.decisionStatus),
      ),
    [apiMappings, approvalQueuedFieldIds],
  );
  const sessionApprovedMappings = useMemo(
    () =>
      apiMappings.filter(
        (mapping) =>
          !approvalQueuedFieldIds.has(String(mapping.id)) &&
          isApprovedAttributeReviewStatus(mapping.decisionStatus),
      ),
    [apiMappings, approvalQueuedFieldIds],
  );
  const unmatchedMappings = useMemo(() => {
    if (reviewSummaryFilter === "LOW_CONFIDENCE") {
      return baseUnmatchedMappings.filter((m) => m.aiBand === "LOW");
    }
    return baseUnmatchedMappings;
  }, [baseUnmatchedMappings, reviewSummaryFilter]);
  const matchedMappings = useMemo(
    () => (reviewSummaryFilter === "LOW_CONFIDENCE" ? [] : baseMatchedMappings),
    [baseMatchedMappings, reviewSummaryFilter],
  );
  const mappings = useMemo(() => {
    if (confidenceSortDirection === null) return unmatchedMappings;
    return sortAttributeReviewMappings(
      unmatchedMappings,
      confidenceSortDirection,
    );
  }, [confidenceSortDirection, unmatchedMappings]);
  const sortedMatchedMappings = useMemo(() => {
    if (confidenceSortDirection === null) return matchedMappings;
    return sortAttributeReviewMappings(
      matchedMappings,
      confidenceSortDirection,
    );
  }, [confidenceSortDirection, matchedMappings]);
  // Selectable rows span Needs Review, Proposed and Approved; each table selects its own rows. Prune
  // selections only for rows that have actually left the list — NOT for rows that
  // merely sit in the Approved bucket — otherwise a just-selected Approved row is
  // stripped from selectedIds on the next render and the checkbox auto-deselects.
  const selectableMappingIds = useMemo(
    () =>
      new Set(
        [...baseUnmatchedMappings, ...baseMatchedMappings].map((mapping) =>
          String(mapping.id),
        ),
      ),
    [baseUnmatchedMappings, baseMatchedMappings],
  );
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(
        Array.from(prev).filter((mappingId) =>
          selectableMappingIds.has(mappingId),
        ),
      );
      return next.size === prev.size ? prev : next;
    });
  }, [selectableMappingIds]);
  // Only prune queued markers for rows that left the session entirely. An
  // approved decisionStatus is NOT a terminal signal (the server sets it at
  // queue time); the pipeline subscription's SUCCESS/FAILED handlers own
  // clearing the set. Parked failure ids are released ONLY when the
  // field-mappings query itself reports data newer than the park time —
  // apiMappings identity also churns for overlays/local state, and a
  // marketplace-specific refetch must not unpin against stale row data.
  useEffect(() => {
    // While the session's payload hasn't loaded, apiMappings is empty and the
    // absent-row prune would strip every freshly re-seeded pin — with nothing
    // to restore them once data arrives (the pipelines haven't changed).
    if (!hasFieldMappingsPayload) return;
    const released: string[] = [];
    const pending = currentSessionId
      ? pendingClearQueuedFieldIdsRef.current.get(currentSessionId)
      : null;
    if (pending && pending.size > 0 && currentSessionId) {
      for (const [mappingId, parkedAt] of pending) {
        if (fieldMappingsFreshlyFetched(currentSessionId, parkedAt)) {
          released.push(mappingId);
        }
      }
      forgetPendingClears(currentSessionId, released);
    }
    setApprovalQueuedFieldIds((prev) => {
      if (prev.size === 0) return prev;
      const currentIds = new Set(
        apiMappings.map((mapping) => String(mapping.id)),
      );
      const next = new Set(
        Array.from(prev).filter(
          (mappingId) =>
            currentIds.has(mappingId) && !released.includes(mappingId),
        ),
      );
      return next.size === prev.size ? prev : next;
    });
    // fieldMappingsDataUpdatedAt: a successful refetch can leave apiMappings
    // structurally identical, so the timestamp is what re-fires the release.
  }, [
    apiMappings,
    currentSessionId,
    fieldMappingsDataUpdatedAt,
    fieldMappingsFreshlyFetched,
    forgetPendingClears,
    hasFieldMappingsPayload,
  ]);
  const context: AttributeReviewContext = useMemo(() => {
    const contextPayload = apiPayload;
    const visibleNeedsReview = sessionActionableMappings.length;
    const visibleApproved = sessionApprovedMappings.length;
    if (contextPayload) {
      // Once item rows exist the FE count is authoritative even at 0 — a stale
      // server count must not paint a badge over an empty section.
      const itemsLoaded = (contextPayload.items?.length ?? 0) > 0;
      const renderedNeedsReview = itemsLoaded
        ? visibleNeedsReview
        : (contextPayload.needReviewFields ?? 0);
      return {
        skuId: contextPayload.mappingSessionId ?? currentSessionId ?? "—",
        mappingRunId: currentSessionId ?? "—",
        modelVersion: contextPayload.schemaVersion ?? "1",
        sourceMarketplace: contextPayload.marketplace ?? "—",
        targetMarketplace: "Canonical",
        productType: contextPayload.productType ?? "—",
        totalAttributes: contextPayload.totalFields ?? 0,
        needsReview: renderedNeedsReview,
        approved: itemsLoaded
          ? visibleApproved
          : Math.max(
              0,
              (contextPayload.mappedFields ?? 0) - renderedNeedsReview,
            ),
      };
    }
    if (currentSessionId || selectedSummarySession) {
      const summaryNeedsReview =
        visibleNeedsReview > 0
          ? visibleNeedsReview
          : (selectedSummarySession?.needReviewFields ?? 0);
      return {
        ...EMPTY_CONTEXT,
        skuId:
          currentSessionId ?? selectedSummarySession?.mappingSessionId ?? "—",
        mappingRunId:
          currentSessionId ?? selectedSummarySession?.mappingSessionId ?? "—",
        sourceMarketplace:
          selectedSummarySession?.marketplace ?? selectedMarketplace ?? "—",
        productType:
          selectedSummarySession?.productType ?? selectedProductType ?? "—",
        totalAttributes: summaryNeedsReview,
        needsReview: summaryNeedsReview,
        approved: visibleApproved,
      };
    }
    return { ...EMPTY_CONTEXT };
  }, [
    apiPayload,
    sessionApprovedMappings.length,
    sessionActionableMappings.length,
    currentSessionId,
    selectedMarketplace,
    selectedProductType,
    selectedSummarySession,
  ]);

  const needsReview = context.needsReview;
  const saveAllBlockers =
    apiMappings.length > 0
      ? countSaveAllBlockers(sessionActionableMappings)
      : needsReview;
  const lowConfidence = useMemo(
    () => sessionActionableMappings.filter((m) => m.aiBand === "LOW").length,
    [sessionActionableMappings],
  );
  const someSelected = selectedIds.size > 0;

  // Anchor for shift-click range selection. Cleared whenever the visible
  // list shifts under us (filter change, refetch reorder) so a stale anchor
  // can't select a phantom range.
  const lastSelectedIdRef = useRef<string | null>(null);

  const handleSelectChange = useCallback(
    (
      id: string,
      checked: boolean,
      shiftKey: boolean,
      visibleIdsOverride?: string[],
    ) => {
      const visibleIds =
        visibleIdsOverride ?? mappings.map((m) => m.id as string);
      const anchorId = lastSelectedIdRef.current;
      const currentIndex = visibleIds.indexOf(id);
      const anchorIndex = anchorId ? visibleIds.indexOf(anchorId) : -1;

      // Range select: shift held, anchor still in the visible list, and the
      // anchor isn't the same row we're clicking.
      if (
        shiftKey &&
        anchorIndex !== -1 &&
        currentIndex !== -1 &&
        anchorIndex !== currentIndex
      ) {
        const [start, end] =
          anchorIndex < currentIndex
            ? [anchorIndex, currentIndex]
            : [currentIndex, anchorIndex];
        const rangeIds = visibleIds.slice(start, end + 1);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          for (const rid of rangeIds) {
            // Shift-range must not sweep rows locked by an in-flight approval.
            if (checked && approvalQueuedFieldIds.has(rid)) continue;
            if (checked) next.add(rid);
            else next.delete(rid);
          }
          return next;
        });
      } else {
        setSelectedIds((prev) => {
          const next = new Set(prev);
          if (checked && approvalQueuedFieldIds.has(id)) return next;
          if (checked) next.add(id);
          else next.delete(id);
          return next;
        });
      }

      lastSelectedIdRef.current = id;
    },
    [approvalQueuedFieldIds, mappings],
  );

  const handleToggleConfidenceSort = useCallback(() => {
    setConfidenceSortDirection((current) => {
      if (current === null) return "desc";
      if (current === "desc") return "asc";
      return null;
    });
  }, []);

  const handleMlSuggestedStatusFilterChange = useCallback((value: string) => {
    setMlSuggestedStatusFilter(value);
    setReviewSummaryFilter("NEEDS_REVIEW");
    setSelectedIds(new Set());
    setExpandedId(null);
  }, []);

  const handleReviewSummaryFilterChange = useCallback(
    (filter: ReviewSummaryFilter) => {
      setReviewSummaryFilter(filter);
      setSelectedIds(new Set());
      setExpandedId(null);
    },
    [],
  );

  const handleDraftReviewChange = useCallback(
    (mappingId: string, draft: AttributeReviewDraftSelection | null) => {
      setDraftReviewSelections((prev) => {
        if (!draft) {
          if (!prev[mappingId]) return prev;
          const next = { ...prev };
          delete next[mappingId];
          return next;
        }
        if (attributeReviewDraftSelectionsEqual(prev[mappingId], draft)) {
          return prev;
        }
        return { ...prev, [mappingId]: draft };
      });
    },
    [],
  );

  const clearDraftSelectionsForIds = useCallback((mappingIds: string[]) => {
    if (mappingIds.length === 0) return;
    setDraftReviewSelections((prev) => {
      const next = { ...prev };
      let changed = false;
      mappingIds.forEach((mappingId) => {
        if (mappingId in next) {
          delete next[mappingId];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, []);

  useEffect(() => {
    if (!currentSessionId || draftStorageSessionId !== currentSessionId) return;
    const saved = apiMappings.filter((mapping) => {
      const id = String(mapping.id);
      const draft = draftReviewSelections[id];
      return (
        draft &&
        (isApprovedAttributeReviewStatus(mapping.decisionStatus) ||
          isRejectedAttributeReviewStatus(mapping.decisionStatus)) &&
        !approvalQueuedFieldIds.has(id) &&
        draftEqualsSavedAnswer(draft, mapping)
      );
    });
    if (saved.length === 0) return;
    const ids = saved.map((mapping) => String(mapping.id));
    clearStoredDrafts(currentSessionId, ids);
    clearDraftSelectionsForIds(ids);
  }, [
    apiMappings,
    approvalQueuedFieldIds,
    clearDraftSelectionsForIds,
    currentSessionId,
    draftReviewSelections,
    draftStorageSessionId,
  ]);

  const clearReviewSelectionState = useCallback(
    (mappingIds?: string[]) => {
      setSelectedIds(new Set());
      setExpandedId(null);
      if (mappingIds) {
        clearDraftSelectionsForIds(mappingIds);
      }
    },
    [clearDraftSelectionsForIds],
  );

  const handleApprovalPipelineUpdate: NonNullable<
    UseTaxonomyApprovalPipelineSubscriptionOptions["onUpdate"]
  > = (update) => {
    const nextState = approvalPipelineStateFromUpdate(update);
    upsertApprovalPipeline(nextState);

    if (!nextState.terminal) return;

    const terminalKey = nextState.syncJobId ?? `${nextState.occurredAt}`;
    if (handledTerminalApprovalJobIdsRef.current.has(terminalKey)) return;
    handledTerminalApprovalJobIdsRef.current.add(terminalKey);

    if (pipelineUpdateHasTerminalSuccess(nextState)) {
      const previousInput = nextState.syncJobId
        ? approvalInputsByJobIdRef.current.get(nextState.syncJobId)
        : null;
      const storedContext = nextState.syncJobId
        ? approvalContextsByJobIdRef.current.get(nextState.syncJobId)
        : null;
      const completedContext = approvalReviewContextFromPipelineState(
        nextState,
        storedContext,
      );
      // Always union results with the stored input: fieldResults carry only
      // FIELD rows, and value-only rows pinned at submit would stay stuck.
      const affectedFieldIds = uniqueStrings([
        ...nextState.fieldResults
          .map((result) => result.sourceFieldId)
          .filter(Boolean),
        ...approveInputAffectedFieldIds(previousInput),
      ]);
      const isSaveAll =
        !!nextState.syncJobId &&
        saveAllJobIdsRef.current.delete(nextState.syncJobId);
      const isSameActiveReviewPage = isSaveAll
        ? nextState.mappingSessionId ===
          activeReviewContextRef.current.mappingSessionId
        : approvalReviewContextsMatch(
            completedContext,
            activeReviewContextRef.current,
          );
      if (isSaveAll) {
        writeStoredDraftSelections(nextState.mappingSessionId, {});
      } else {
        clearStoredDrafts(nextState.mappingSessionId, affectedFieldIds);
      }
      if (nextState.mappingSessionId === currentSessionId) {
        clearReviewSelectionState(affectedFieldIds);
        if (isSaveAll) setDraftReviewSelections({});
      }
      showToast.success(
        "Approval complete",
        nextState.message ??
          "Selected mappings were approved and refreshed from taxonomy.",
      );
      refreshAttributeReviewFilters();
      if (isSameActiveReviewPage) {
        setSelectedSessionId(null);
        removeSessionIdFromUrl();
        setLastKnownReviewSession((prev) =>
          prev?.mappingSessionId === nextState.mappingSessionId ||
          approvalReviewContextsMatch(completedContext, prev ?? {})
            ? null
            : prev,
        );
        setLastUsableFieldMappingsPayload((prev) =>
          prev?.mappingSessionId === nextState.mappingSessionId ? null : prev,
        );
        refreshAttributeReviewData(nextState.mappingSessionId);
        // Field ids repeat across sessions and context matching can pass on
        // marketplace+productType alone — only the exact session's job may
        // release the pins.
        if (
          nextState.mappingSessionId ===
          activeReviewContextRef.current.mappingSessionId
        ) {
          setApprovalQueuedFieldIds((prev) =>
            removeStringsFromSet(prev, affectedFieldIds),
          );
        }
        forgetPendingClears(nextState.mappingSessionId, affectedFieldIds);
        setApprovedBucket({});
      }
      if (nextState.syncJobId) {
        approvalInputsByJobIdRef.current.delete(nextState.syncJobId);
        approvalContextsByJobIdRef.current.delete(nextState.syncJobId);
      }
      return;
    }

    if (pipelineUpdateHasTerminalFailure(nextState)) {
      // The optimistic approve decrement assumed success; on terminal failure the
      // fields never left review, so refetch the session summaries to restore the
      // true chip counts.
      void queryClient.invalidateQueries({
        queryKey: ["AttributeReviewSessionsUnderReview"],
      });
      // Failed rows return to Need Review, but only AFTER the refetch below
      // settles — the cached decisionStatus still says approved (the server
      // flips it at queue time), so un-pinning first would flash the row
      // into Approved and make it resubmittable while the request is
      // pending.
      const failedInput = nextState.syncJobId
        ? approvalInputsByJobIdRef.current.get(nextState.syncJobId)
        : null;
      const failedFieldIds = uniqueStrings([
        ...nextState.fieldResults
          .map((result) => result.sourceFieldId)
          .filter(Boolean),
        ...approveInputAffectedFieldIds(failedInput),
      ]);
      const clearFailedQueuedIds = () => {
        if (failedFieldIds.length === 0) return;
        forgetPendingClears(nextState.mappingSessionId, failedFieldIds);
        // Field ids repeat across sessions — a background session's job
        // must never unlock rows in the session on screen.
        if (
          nextState.mappingSessionId ===
          activeReviewContextRef.current.mappingSessionId
        ) {
          setApprovalQueuedFieldIds((prev) =>
            removeStringsFromSet(prev, failedFieldIds),
          );
        }
      };
      const fallbackToFailureToast = () => {
        showToast.error(
          getApprovalPipelineStatusLabel(nextState.status),
          firstFailedMessage(nextState.fieldResults, nextState.valueResults) ??
            getApprovalPipelineFailureMessage(nextState),
        );
      };
      // The server flips decisionStatus at queue time, so after a failure the
      // cached row can still read approved — refetch so the returned row
      // carries the server's true post-failure status, and un-pin only once
      // that refetch settles.
      // Always refresh the FAILED session's cache — even when the reviewer
      // has another page open — or returning within the stale window would
      // render the cached queue-time APPROVED row. Only the pin bookkeeping
      // is scoped to the session on screen (clearFailedQueuedIds gates
      // internally; the parking branch gates explicitly). "Settled" alone
      // can't confirm fresh data — allSettled never rejects and an
      // invalidate swallows refetch errors — so clear only when the
      // field-mappings cache actually updated after this point, and park
      // otherwise for the release effect.
      // Park BEFORE the refresh, not after it fails: a session switch while
      // the request is in flight would otherwise find neither an active
      // pipeline nor a parked entry, leaving the stale row unlocked.
      const refreshStartedAt = Date.now();
      parkPendingClears(
        nextState.mappingSessionId,
        failedFieldIds,
        refreshStartedAt,
      );
      void refreshAttributeReviewData(nextState.mappingSessionId).then(() => {
        // Confirmed fresh → forget the park and unlock; otherwise the park
        // stands and the release effect frees them when data lands.
        if (
          fieldMappingsFreshlyFetched(
            nextState.mappingSessionId,
            refreshStartedAt,
          )
        ) {
          clearFailedQueuedIds();
        }
      });
      fallbackToFailureToast();
      return;
    }

    // Any other terminal status (e.g. SKIPPED) has no dedicated branch,
    // but the job is over and no further event will come — release the
    // job's pins and drop its stored input so the rows unlock.
    const otherTerminalInput = nextState.syncJobId
      ? approvalInputsByJobIdRef.current.get(nextState.syncJobId)
      : null;
    const otherTerminalFieldIds = uniqueStrings([
      ...nextState.fieldResults
        .map((result) => result.sourceFieldId)
        .filter(Boolean),
      ...approveInputAffectedFieldIds(otherTerminalInput),
    ]);
    if (otherTerminalFieldIds.length > 0) {
      // Refresh the job's session for EVERY terminal status — even when
      // another page is open, so revisiting can't render the cached
      // queue-time APPROVED row. Same stale-refresh parking as terminal
      // failures, keyed by the job's own session.
      // Park before the refresh for the same reason as the failure path.
      const otherTerminalRefreshStartedAt = Date.now();
      parkPendingClears(
        nextState.mappingSessionId,
        otherTerminalFieldIds,
        otherTerminalRefreshStartedAt,
      );
      void refreshAttributeReviewData(nextState.mappingSessionId).then(() => {
        if (
          fieldMappingsFreshlyFetched(
            nextState.mappingSessionId,
            otherTerminalRefreshStartedAt,
          )
        ) {
          forgetPendingClears(
            nextState.mappingSessionId,
            otherTerminalFieldIds,
          );
          if (
            nextState.mappingSessionId ===
            activeReviewContextRef.current.mappingSessionId
          ) {
            setApprovalQueuedFieldIds((prev) =>
              removeStringsFromSet(prev, otherTerminalFieldIds),
            );
          }
        }
      });
    }
    if (nextState.syncJobId) {
      saveAllJobIdsRef.current.delete(nextState.syncJobId);
      approvalInputsByJobIdRef.current.delete(nextState.syncJobId);
      approvalContextsByJobIdRef.current.delete(nextState.syncJobId);
    }
  };

  useTaxonomyApprovalPipelineSubscription({
    syncJobIds: activeApprovalPipelineSyncJobIds,
    enabled: activeApprovalPipelineSyncJobIds.length > 0,
    onUpdate: handleApprovalPipelineUpdate,
    onError: (_error) => {
      showToast.warning(
        "Approval updates interrupted",
        "The approval request may still be running. Refreshing review filters.",
      );
      refreshAttributeReviewFilters();
    },
  });

  // The subscription is the fast path; this is the one that cannot silently
  // fail. Terminal events cross three processes to get here, and a dropped one
  // used to strand the row behind "Approving…" forever. Same handler, so the
  // existing terminal de-duplication ignores whichever arrives second.
  useApprovalPipelineJobFallbackPoll({
    syncJobIds: activeApprovalPipelineSyncJobIds,
    enabled: activeApprovalPipelineSyncJobIds.length > 0,
    onTerminal: handleApprovalPipelineUpdate,
  });

  const buildFieldDecision = useCallback(
    (mapping: AttributeReviewMappingRow): AttributeReviewFieldDecisionInput =>
      fieldDecisionFor(
        String(mapping.id),
        draftReviewSelections[String(mapping.id)],
      ),
    [draftReviewSelections],
  );

  // Last approval-failure message, so the per-row Save button can show it inline
  // in the popup (submitFieldDecisions otherwise surfaces errors only via toast).
  const lastApprovalErrorRef = useRef<string | null>(null);
  const submitFieldDecisions = useCallback(
    async (
      fieldDecisions: AttributeReviewFieldDecisionInput[],
    ): Promise<boolean> => {
      lastApprovalErrorRef.current = null;
      if (!currentSessionId) {
        showToast.error(
          APPROVE_COPY.missingSessionTitle,
          APPROVE_COPY.missingSessionDescription,
        );
        return false;
      }
      if (!marketplaceId) {
        showToast.error(
          APPROVE_COPY.errorTitle,
          "Marketplace context is missing for this approval.",
        );
        return false;
      }
      if (fieldDecisions.length === 0) {
        showToast.error(
          "No mappings selected",
          "Select at least one mapping first.",
        );
        return false;
      }

      // No client-side enum-mapping pre-check. The engine owns value resolution:
      // on approve it auto-resolves every pending value or parks the ones it
      // cannot (they round-trip as their literal source value), so a missing
      // mapping must never block the submit.

      try {
        const submittedFieldIds = uniqueStrings(
          fieldDecisions.map((decision) => String(decision.sourceFieldId)),
        );
        const input: AttributeReviewApproveSelectedInput = {
          sessionId: currentSessionId,
          marketplaceId,
          fieldDecisions,
          notes: `${APPROVE_COPY.notesVerb} ${fieldDecisions.length} selected mapping(s)`,
          idempotencyKey: createApprovalIdempotencyKey(),
        };
        const approvalContext: ApprovalReviewContext = {
          mappingSessionId: currentSessionId,
          marketplaceId,
          marketplace,
          productType:
            apiPayloadProductType ??
            selectedProductType ??
            currentSession?.productType,
        };
        const result = await approveSelectedMutation.mutateAsync({ input });
        const payload = result.attributeReviewApproveSelected;
        if (!payload) {
          showToast.error(
            APPROVE_COPY.errorTitle,
            "Approval did not return a result payload. Please retry.",
          );
          return false;
        }
        const nextState = approvalPipelineStateFromMutation(payload);
        upsertApprovalPipeline(nextState);
        if (payload.syncJobId && !nextState.terminal) {
          approvalInputsByJobIdRef.current.set(payload.syncJobId, input);
          approvalContextsByJobIdRef.current.set(
            payload.syncJobId,
            approvalContext,
          );
        }

        const normalizedStatus = normalizeApprovalPipelineStatus(
          payload.status,
        );
        const firstResultMessage =
          payload.fieldResults.map(resultMessage).find(Boolean) ??
          payload.valueResults.map(resultMessage).find(Boolean);
        if (normalizedStatus === "FAILED" || payload.failedFieldCount > 0) {
          const failureMessage =
            firstFailedMessage(payload.fieldResults, payload.valueResults) ??
            "Approval was accepted but one or more rows failed validation.";
          lastApprovalErrorRef.current = failureMessage;
          showToast.error(APPROVE_COPY.errorTitle, failureMessage);
          // A terminal payload gets no subscription follow-up — refetch here
          // or the rows keep their stale queue-time status.
          if (nextState.terminal && currentSessionId) {
            void refreshAttributeReviewData(currentSessionId);
          }
          return false;
        }

        const clearedFieldIds = submittedFieldIds;
        // Only a LIVE queued job takes the optimistic path (pin + decrement +
        // "queued" toast). A terminal mutation payload never enters the
        // subscription, so it falls through to the terminal handling below —
        // status-accurate toast, immediate refetch, and no pin nothing would
        // ever release.
        if (payload.syncJobId && !nextState.terminal) {
          // Visible-set writes only while this session is still on screen —
          // field ids repeat across sessions and the session-gated terminal
          // handler could never clear a foreign session's markers. The
          // active-pipeline effect re-pins these on revisit.
          if (
            currentSessionId === activeReviewContextRef.current.mappingSessionId
          ) {
            setApprovalQueuedFieldIds((prev) =>
              addStringsToSet(prev, clearedFieldIds),
            );
          }
          // Optimistically drop the approved fields from the session's need-review
          // tally so the marketplace/product-type chips track the rendered count
          // without waiting for a refetch.
          if (currentSessionId && submittedFieldIds.length > 0) {
            queryClient.setQueriesData<AttributeReviewSessionsUnderReviewQuery>(
              { queryKey: ["AttributeReviewSessionsUnderReview"] },
              (current) => {
                const sessions = current?.attributeReviewSessionsUnderReview;
                if (!sessions) return current;
                const index = sessions.findIndex(
                  (session) => session.mappingSessionId === currentSessionId,
                );
                if (index === -1) return current;
                const target = sessions[index];
                const nextValue = Math.max(
                  0,
                  (target.needReviewFields ?? 0) - submittedFieldIds.length,
                );
                // Return the cached value untouched when nothing changes, so React
                // Query doesn't notify subscribers for a no-op update.
                if (nextValue === (target.needReviewFields ?? 0))
                  return current;
                const nextSessions = sessions.slice();
                nextSessions[index] = {
                  ...target,
                  needReviewFields: nextValue,
                };
                return {
                  ...current,
                  attributeReviewSessionsUnderReview: nextSessions,
                };
              },
            );
          }
          clearReviewSelectionState();
          setApprovedBucket({});
          showToast.success(
            APPROVE_COPY.queuedTitle,
            APPROVE_COPY.queuedDescription,
          );
          return true;
        }

        // Every payload reaching here is terminal — report by its REAL
        // status; a synchronously cancelled/skipped approve is not a success.
        const fallthroughSucceeded = normalizedStatus === "SUCCESS";
        if (fallthroughSucceeded) {
          showToast.success(
            getApprovalPipelineStatusLabel(payload.status),
            firstResultMessage ??
              "Approval finished without a pipeline job. Refreshing review data.",
          );
        } else {
          showToast.error(
            getApprovalPipelineStatusLabel(payload.status),
            firstResultMessage ??
              "The approval did not complete. Refreshing review data.",
          );
        }
        if (fallthroughSucceeded) {
          clearStoredDrafts(currentSessionId, clearedFieldIds);
          clearReviewSelectionState(clearedFieldIds);
        } else {
          clearReviewSelectionState();
        }
        setApprovedBucket({});
        refreshAttributeReviewData(currentSessionId);
        return fallthroughSucceeded;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : APPROVE_COPY.errorFallback;
        lastApprovalErrorRef.current = message;
        showToast.error(APPROVE_COPY.errorTitle, message);
        return false;
      }
    },
    [
      approveSelectedMutation,
      apiPayloadProductType,
      clearReviewSelectionState,
      currentSession?.productType,
      currentSessionId,
      marketplace,
      marketplaceId,
      queryClient,
      refreshAttributeReviewData,
      selectedProductType,
      upsertApprovalPipeline,
    ],
  );

  const rejectFieldMapping = useCallback(
    async (mapping: AttributeReviewMappingRow): Promise<boolean> => {
      const sourceFieldId = String(mapping.id ?? "");
      if (!sourceFieldId) return false;
      const ok = await submitFieldDecisions([
        { sourceFieldId, decision: "IGNORE" },
      ]);
      if (!ok) return false;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(sourceFieldId);
        return next;
      });
      if (expandedId === sourceFieldId) setExpandedId(null);
      return true;
    },
    [submitFieldDecisions, expandedId],
  );

  const unrejectFieldMapping = useCallback(
    async (mapping: AttributeReviewMappingRow): Promise<boolean> => {
      const sourceFieldId = String(mapping.id ?? "");
      if (!sourceFieldId || !currentSessionId) return false;
      const ok = await submitFieldDecisions([
        { sourceFieldId, decision: "CLEAR" },
      ]);
      if (!ok) return false;
      refreshAttributeReviewData(currentSessionId);
      void queryClient.invalidateQueries({
        queryKey: ["AttributeReviewSessionsUnderReview"],
      });
      return true;
    },
    [
      currentSessionId,
      queryClient,
      refreshAttributeReviewData,
      submitFieldDecisions,
    ],
  );

  const selectedNewMappings = useMemo(
    () =>
      baseUnmatchedMappings.filter(
        (mapping) =>
          selectedIds.has(String(mapping.id)) &&
          !approvalQueuedFieldIds.has(String(mapping.id)),
      ),
    [approvalQueuedFieldIds, baseUnmatchedMappings, selectedIds],
  );

  const handleReviewSelected = useCallback(async (): Promise<boolean> => {
    const fieldDecisions = selectedNewMappings.map((mapping) =>
      buildFieldDecision(mapping),
    );

    return submitFieldDecisions(fieldDecisions);
  }, [buildFieldDecision, selectedNewMappings, submitFieldDecisions]);

  // Modal-driven approve: stage the decision into approvedBucket without
  // hitting submitFieldDecisions. The bulk save runs once when the modal
  // closes (or when the last pending row is approved).
  // Returns the staged decision on success so the modal can either advance to
  // the next pending row or immediately commit the final row.
  const stageApproval = useCallback(
    async (
      mapping: AttributeReviewMappingRow,
    ): Promise<AttributeReviewStagedApproval | null> => {
      const fieldDecision = buildFieldDecision(mapping);
      // No client-side enum-mapping pre-check (the engine owns value resolution).
      const stagedApproval = { fieldDecision };
      setApprovedBucket((prev) => ({
        ...prev,
        [String(mapping.id)]: stagedApproval,
      }));
      return stagedApproval;
    },
    [buildFieldDecision],
  );

  // One-shot save used by the inline tick and the modal's per-row Save. Skips
  // the approvedBucket so the row never enters the "Revert (staged)" state, and
  // returns the failure message so the popup can show it inline.
  const approveRowImmediately = useCallback(
    async (
      mapping: AttributeReviewMappingRow,
    ): Promise<{ ok: boolean; error: string | null }> => {
      lastApprovalErrorRef.current = null;
      const fail = () => ({
        ok: false,
        error:
          lastApprovalErrorRef.current ??
          "Save failed. Review the highlighted values and try again.",
      });
      const fieldDecision = buildFieldDecision(mapping);
      const ok = await submitFieldDecisions([fieldDecision]);
      return ok ? { ok, error: null } : fail();
    },
    [buildFieldDecision, submitFieldDecisions],
  );

  const commitApprovedBucket = useCallback(
    async (
      immediateDecisions: AttributeReviewStagedApproval[] = [],
    ): Promise<boolean> => {
      const decisionsByFieldId = new Map<
        string,
        AttributeReviewFieldDecisionInput
      >();

      Object.values(approvedBucket).forEach((stagedApproval) => {
        decisionsByFieldId.set(
          String(stagedApproval.fieldDecision.sourceFieldId),
          stagedApproval.fieldDecision,
        );
      });
      immediateDecisions.forEach((stagedApproval) => {
        decisionsByFieldId.set(
          String(stagedApproval.fieldDecision.sourceFieldId),
          stagedApproval.fieldDecision,
        );
      });
      const fieldDecisions = Array.from(decisionsByFieldId.values());
      if (fieldDecisions.length === 0) return false;
      return submitFieldDecisions(fieldDecisions);
    },
    [approvedBucket, submitFieldDecisions],
  );

  const retryApprovalPipeline = useCallback(async (): Promise<boolean> => {
    if (!retryableApprovalPipeline?.retryable) {
      showToast.error(
        "Retry unavailable",
        "This approval failure is not marked retryable by the server.",
      );
      return false;
    }
    const previousInput = retryableApprovalPipeline.syncJobId
      ? approvalInputsByJobIdRef.current.get(
          retryableApprovalPipeline.syncJobId,
        )
      : null;
    const previousContext = retryableApprovalPipeline.syncJobId
      ? approvalContextsByJobIdRef.current.get(
          retryableApprovalPipeline.syncJobId,
        )
      : null;
    if (!previousInput) {
      showToast.error(
        "Retry unavailable",
        "The previous approval request is no longer available. Select the rows and approve again.",
      );
      return false;
    }
    if (approveSelectedMutation.isPending) {
      showToast.error(
        "Approval already running",
        "Please wait for the current save request to queue before retrying.",
      );
      return false;
    }

    try {
      const input: AttributeReviewApproveSelectedInput = {
        ...previousInput,
        idempotencyKey: createApprovalIdempotencyKey(),
      };
      const result = await approveSelectedMutation.mutateAsync({ input });
      const payload = result.attributeReviewApproveSelected;
      if (!payload) {
        showToast.error(
          "Retry failed",
          "Approval retry did not return a result payload.",
        );
        return false;
      }
      const nextState = approvalPipelineStateFromMutation(payload);
      upsertApprovalPipeline(nextState);
      const retriedFieldIds = approveInputAffectedFieldIds(input);
      if (nextState.terminal) {
        // Terminal with OR without a job id (a synchronous response has no
        // id and is always terminal): no subscription follows, so hand any
        // inherited pins to the parked-release path, refetch the truth, and
        // report the payload's real outcome.
        const retryRefreshStartedAt = Date.now();
        parkPendingClears(
          input.sessionId,
          retriedFieldIds,
          retryRefreshStartedAt,
        );
        void refreshAttributeReviewData(input.sessionId).then(() => {
          // Clear directly on a confirmed refetch — a successful retry often
          // leaves the payload structurally identical, so the
          // apiMappings-driven release effect may never re-fire for it.
          if (
            !fieldMappingsFreshlyFetched(input.sessionId, retryRefreshStartedAt)
          ) {
            return;
          }
          forgetPendingClears(input.sessionId, retriedFieldIds);
          if (
            input.sessionId === activeReviewContextRef.current.mappingSessionId
          ) {
            setApprovalQueuedFieldIds((prev) =>
              removeStringsFromSet(prev, retriedFieldIds),
            );
          }
        });
        const terminalMessage =
          payload.fieldResults.map(resultMessage).find(Boolean) ??
          payload.valueResults.map(resultMessage).find(Boolean);
        const retrySucceeded =
          normalizeApprovalPipelineStatus(payload.status) === "SUCCESS";
        if (retrySucceeded) {
          showToast.success(
            getApprovalPipelineStatusLabel(payload.status),
            terminalMessage ?? "Retry completed.",
          );
        } else {
          showToast.error(
            getApprovalPipelineStatusLabel(payload.status),
            firstFailedMessage(payload.fieldResults, payload.valueResults) ??
              terminalMessage ??
              "The retry did not complete.",
          );
        }
        return retrySucceeded;
      }
      if (payload.syncJobId) {
        approvalInputsByJobIdRef.current.set(payload.syncJobId, input);
        if (previousContext) {
          approvalContextsByJobIdRef.current.set(
            payload.syncJobId,
            previousContext,
          );
        }
        // Purge stale parked entries first: leftovers from the failed
        // attempt carry old timestamps that any successful fetch satisfies,
        // and the release effect would unlock the rows mid-retry.
        forgetPendingClears(input.sessionId, retriedFieldIds);
        // Re-pin as Approving… and drop the rows from the selection — a
        // checked-but-disabled selection could otherwise steer Approve
        // Selected into the full-session fallback. Visible-set writes only
        // while this session is still on screen (the active-pipeline effect
        // re-pins it on revisit).
        if (
          input.sessionId === activeReviewContextRef.current.mappingSessionId
        ) {
          setApprovalQueuedFieldIds((prev) =>
            addStringsToSet(prev, retriedFieldIds),
          );
          setSelectedIds((prev) => removeStringsFromSet(prev, retriedFieldIds));
        }
      }
      const firstResultMessage =
        payload.fieldResults.map(resultMessage).find(Boolean) ??
        payload.valueResults.map(resultMessage).find(Boolean);
      showToast.success(
        getApprovalPipelineStatusLabel(payload.status),
        payload.syncJobId
          ? "Retry accepted. Watching the approval pipeline."
          : (firstResultMessage ?? "Retry accepted."),
      );
      return true;
    } catch (error) {
      showToast.error(
        "Retry failed",
        error instanceof Error
          ? error.message
          : "Could not retry the approval pipeline.",
      );
      return false;
    }
  }, [
    approveSelectedMutation,
    fieldMappingsFreshlyFetched,
    forgetPendingClears,
    parkPendingClears,
    refreshAttributeReviewData,
    retryableApprovalPipeline,
    upsertApprovalPipeline,
  ]);

  const discardApprovedBucket = useCallback(() => {
    setApprovedBucket({});
  }, []);

  const revertStagedApproval = useCallback((mappingId: string) => {
    setApprovedBucket((prev) => {
      if (!(mappingId in prev)) return prev;
      const next = { ...prev };
      delete next[mappingId];
      return next;
    });
  }, []);

  const approvedBucketIds = useMemo(
    () => new Set(Object.keys(approvedBucket)),
    [approvedBucket],
  );
  const approvedBucketSize = approvedBucketIds.size;

  // Finalize the whole product. Approving individual attributes only commits the
  // rows; it never tells the taxonomy engine the SESSION is done, so the product
  // stays in "Needs Review" forever even at 100%. attributeReviewApproveSession
  // is the session-level APPROVE that flips the session to mapped, stamps
  // mapped-at/by, and drops it out of the review list. Only actionable once every
  // attribute is approved or auto-approvable (saveAllBlockers === 0).
  const completeReview = useCallback(async (): Promise<boolean> => {
    if (approveSessionMutation.isPending) return false;
    if (!currentSessionId) {
      showToast.error("Cannot save all", "No active session is selected.");
      return false;
    }
    if (saveAllBlockers > 0) {
      showToast.error(
        "Review not complete",
        `${saveAllBlockers} attribute${saveAllBlockers === 1 ? "" : "s"} still need review before Save all.`,
      );
      return false;
    }
    // Pinned rows outlive a terminal pipeline until their refetch confirms —
    // finalizing the whole session while they show Approving… races them.
    if (approvalQueuedFieldIds.size > 0) {
      showToast.error(
        "Approvals still settling",
        "Some rows are still finishing their approval job. Try again in a moment.",
      );
      return false;
    }
    const approvalContext = { ...activeReviewContextRef.current };
    try {
      // Full-session finalize: send decision APPROVE with NO fieldDecisions.
      // The taxonomy engine then approves EVERY field — including the trusted
      // high-confidence auto-matches the reviewer never explicitly clicked —
      // which is what drives the session out of NEEDS_APPROVAL and onto
      // "Mapped". Sending per-field decisions instead takes the engine's
      // PARTIAL path, which leaves un-acted fields counted as remaining and the
      // product stuck in Need Review at 0 need-review (the misleading state).
      const result = await approveSessionMutation.mutateAsync({
        input: {
          sessionId: currentSessionId,
          decision: "APPROVE",
          notes: "Complete review — approve all remaining attributes",
        },
      });
      const syncJobId = result.attributeReviewApproveSession?.syncJobId;
      if (!syncJobId) {
        showToast.error(
          "Not saved",
          "The server did not return a job for Save all.",
        );
        return false;
      }
      upsertApprovalPipeline(
        approvalPipelineStateFromMutation({
          mappingSessionId: currentSessionId,
          syncJobId,
          status: "QUEUED",
          retryable: false,
          fieldResults: [],
          valueResults: [],
        }),
      );
      approvalContextsByJobIdRef.current.set(syncJobId, approvalContext);
      saveAllJobIdsRef.current.add(syncJobId);
      showToast.info(
        "Saving all rows…",
        `Job ${syncJobId}. The page updates when it ends.`,
      );
      refreshAttributeReviewData(currentSessionId);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Save all failed.";
      showToast.error("Save all failed", message);
      void refreshAttributeReviewData(currentSessionId);
      return false;
    }
  }, [
    approvalQueuedFieldIds,
    approveSessionMutation,
    currentSessionId,
    refreshAttributeReviewData,
    saveAllBlockers,
    upsertApprovalPipeline,
  ]);

  // Re-runs the server-persisted failed job from where the sync stopped; approved rows are kept.
  const resumeFailedApprovalSync = useCallback(async (): Promise<boolean> => {
    const syncJobId = resumableFailedApprovalJob?.syncJobId;
    if (!syncJobId) {
      showToast.error(
        "Resume unavailable",
        "No resumable approval job was found for this session. Re-run the product type sync instead.",
      );
      return false;
    }
    if (retryApprovalJobMutation.isPending) return false;
    try {
      const result = await retryApprovalJobMutation.mutateAsync({
        input: { syncJobId, auditNote: "Resume approval sync from failure" },
      });
      showToast.success(
        "Approval resumed",
        result.retryTaxonomyApprovalPipelineJob?.job
          ? "The failed approval sync is retrying from where it stopped."
          : "Resume accepted.",
      );
      if (currentSessionId) {
        refreshAttributeReviewData(currentSessionId);
      }
      void queryClient.invalidateQueries({
        queryKey: useTaxonomyApprovalPipelineJobsQuery.getKey({
          input: { mappingSessionId: currentSessionId ?? undefined },
        }),
      });
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Resume failed.";
      showToast.error("Resume failed", message);
      return false;
    }
  }, [
    resumableFailedApprovalJob,
    retryApprovalJobMutation,
    currentSessionId,
    refreshAttributeReviewData,
    queryClient,
  ]);

  const handleDeleteSession = useCallback(async () => {
    if (!currentSessionId) {
      showToast.error("Delete failed", "No active session selected");
      return;
    }
    if (deleteMappingSessionMutation.isPending) return; // guard against re-entry
    try {
      await deleteMappingSessionMutation.mutateAsync({
        input: { sessionId: currentSessionId },
      });
      // The deleted session is gone - drop its sessionId from the URL so the
      // deep link no longer points at a now-deleted session. onSuccess clears
      // the in-memory selection.
      removeSessionIdFromUrl();
    } catch {
      // onError already surfaces a toast; swallow here so `void
      // handleDeleteSession()` callers don't get an unhandled rejection.
    }
  }, [currentSessionId, deleteMappingSessionMutation, removeSessionIdFromUrl]);

  const setExpandedIdHandler = useCallback((id: string | null) => {
    setExpandedId(id);
  }, []);

  const toggleExpanded = useCallback((mappingId: string) => {
    setExpandedId((current) => (current === mappingId ? null : mappingId));
  }, []);

  return {
    // Filters & session
    sessionsLoading,
    sessionsUnderReview,
    currentSessionId,
    currentSessionStatus,
    isCurrentSessionApprovalSyncFailed,
    approvalSyncError,
    approvalSyncFieldErrors,
    marketplaces,
    productTypes,
    selectedMarketplace,
    selectedProductType,
    setSelectedMarketplace: toggleMarketplace,
    setSelectedProductType: toggleProductType,

    // Mappings & context
    context,
    mappings,
    matchedMappings: sortedMatchedMappings,
    hasFieldMappingsPayload,
    fieldMappingsLoading,
    fieldMappingsEmpty,
    fieldMappingsError,
    needsReview,
    saveAllBlockers,
    lowConfidence,
    reviewSummaryFilter,
    confidenceSortDirection,
    mlSuggestedStatusFilter,
    mlSuggestedStatusFilterOptions,
    setMlSuggestedStatusFilter: handleMlSuggestedStatusFilterChange,
    setReviewSummaryFilter: handleReviewSummaryFilterChange,

    // Selection
    selectedIds,
    approvalQueuedFieldIds,
    someSelected,
    draftReviewSelections,
    handleToggleConfidenceSort,
    handleSelectChange,
    setSelectedIds,

    // Row expansion
    expandedId,
    setExpandedId: setExpandedIdHandler,
    toggleExpanded,

    // Actions
    stageApproval,
    approveRowImmediately,
    commitApprovedBucket,
    discardApprovedBucket,
    revertStagedApproval,
    approvedBucketIds,
    approvedBucketSize,
    isCommittingBucket: approveSelectedMutation.isPending,
    approvalPipeline,
    activeApprovalPipelines,
    activeApprovalPipelineCount: approvalPipelineActiveCount,
    retryApprovalPipeline,
    handleDraftReviewChange,
    rejectFieldMapping,
    unrejectFieldMapping,
    handleReviewSelected,
    selectedNewCount: selectedNewMappings.length,
    completeReview,
    resumeFailedApprovalSync,
    canResumeFailedApprovalSync: Boolean(resumableFailedApprovalJob),
    isResumingFailedApprovalSync: retryApprovalJobMutation.isPending,
    handleDeleteSession,
    isDeletingSession: deleteMappingSessionMutation.isPending,
    isSaving: approveSelectedMutation.isPending,
  };
}
